import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLang } from "../context/LangContext";

function buildColumns(t, variant, kind = "factory") {
    if (kind === "client") {
        return [
            { key: "karat_type", label: t.colKaratType, className: "w-[10%]", numeric: false },
            { key: "weight", label: t.colWeight, className: "w-[9%]", numeric: true },
            { key: "karat", label: t.colKarat, className: "w-[8%]", numeric: true },
            { key: "result", label: t.colResult, className: "w-[11%] bg-amber-50/60", numeric: true, computed: true },
            { key: "description", label: t.colDescription, className: "w-[40%]", numeric: false },
            { key: "date", label: t.colDate, className: "w-[14%]", numeric: false },
        ];
    }
    if (variant === "credit") {
        return [
            { key: "karat_type", label: t.colKaratType, className: "w-[11%]", numeric: false },
            { key: "weight", label: t.colWeight, className: "w-[9%]", numeric: true },
            { key: "karat", label: t.colKarat, className: "w-[9%]", numeric: true },
            { key: "result", label: t.colResult, className: "w-[11%] bg-amber-50/60", numeric: true, computed: true },
            { key: "manufacturing_fee", label: t.colManufacturing, className: "w-[11%]", numeric: true },
            { key: "description", label: t.colDescription, className: "w-[19%]", numeric: false },
            { key: "invoice_number", label: t.colInvoice, className: "w-[14%]", numeric: false },
            { key: "fees", label: t.colFees, className: "w-[12%] bg-emerald-50/40", numeric: true },
        ];
    }
    return [
        { key: "karat_type", label: t.colKaratType, className: "w-[11%]", numeric: false },
        { key: "weight", label: t.colWeight, className: "w-[9%]", numeric: true },
        { key: "karat", label: t.colKarat, className: "w-[9%]", numeric: true },
        { key: "result", label: t.colResult, className: "w-[11%] bg-amber-50/60", numeric: true, computed: true },
        { key: "gold_received", label: t.colGoldReceived, className: "w-[11%]", numeric: false },
        { key: "description", label: t.colDescription, className: "w-[19%]", numeric: false },
        { key: "invoice_number", label: t.colInvoice, className: "w-[14%]", numeric: false },
        { key: "fees", label: t.colFees, className: "w-[12%]", numeric: true },
    ];
}

function computeResult(row) {
    const w = parseFloat(row.weight);
    const k = parseFloat(row.karat);
    const t = parseFloat(row.karat_type);
    if (!isFinite(w) || !isFinite(k) || !isFinite(t) || t === 0) return "";
    const v = (w * k) / t;
    if (!isFinite(v)) return "";
    return parseFloat(v.toFixed(3)).toString();
}

export default function LedgerTable({
    title,
    rows,
    onChange,
    variant = "credit",
    readOnly = false,
    testIdPrefix = "credit",
    kind = "factory",
    karatLock = null,
}) {
    const headerClass = variant === "credit" ? "band-credit" : "band-debit";
    const { t } = useLang();
    const COLUMNS = useMemo(() => buildColumns(t, variant, kind), [t, variant, kind]);

    const editableCols = useMemo(() => COLUMNS.filter((c) => !c.computed), [COLUMNS]);
    const totalCols = editableCols.length;
    const totalRows = rows.length;

    const tableRef = useRef(null);
    const [focusPos, setFocusPos] = useState(null); // {row, col}

    const updateRow = (idx, key, value) => {
        const next = rows.map((r, i) => {
            if (i !== idx) return r;
            // If karat_type is locked for this account, silently ignore any
            // attempt to overwrite it — belt-and-braces alongside the
            // read-only UI.
            if (karatLock && key === "karat_type") return r;
            const updated = { ...r, [key]: value };
            if (karatLock && !updated.karat_type) updated.karat_type = karatLock;
            if (
                kind === "factory" &&
                variant === "credit" &&
                ["weight", "karat", "karat_type", "manufacturing_fee"].includes(key)
            ) {
                // Wages formula (requested 2026-02-25):
                //   fees = weight × manufacturing_fee
                // (no longer multiplied by "naatij"). Naatij still shown.
                const w = parseFloat(updated.weight);
                const mf = parseFloat(updated.manufacturing_fee);
                if (isFinite(w) && isFinite(mf)) {
                    const fee = w * mf;
                    updated.fees = parseFloat(fee.toFixed(3)).toString();
                }
            }
            return updated;
        });
        onChange(next);
    };

    const addRow = () => {
        onChange([
            ...rows,
            {
                id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                karat_type: karatLock || "",
                weight: "",
                karat: "",
                description: "",
                invoice_number: "",
                manufacturing_fee: "",
                gold_received: "",
                fees: "",
                date: "",
            },
        ]);
    };

    const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

    const totalFees = rows.reduce((sum, r) => {
        const v = parseFloat(r.fees);
        return isFinite(v) ? sum + v : sum;
    }, 0);

    const focusCell = (r, c) => {
        if (!tableRef.current) return;
        const el = tableRef.current.querySelector(
            `input[data-cell-row="${r}"][data-cell-col="${c}"]`
        );
        if (el) {
            el.focus();
            try {
                el.select();
            } catch {
                /* ignore */
            }
        }
    };

    const move = (direction) => {
        if (!focusPos) return;
        const { row, col } = focusPos;
        let nr = row;
        let nc = col;
        if (direction === "up") nr = Math.max(0, row - 1);
        else if (direction === "down") nr = Math.min(totalRows - 1, row + 1);
        else if (direction === "next") {
            // RTL forward = visually left = next cell in row, then first cell of next row
            if (col < totalCols - 1) nc = col + 1;
            else if (row < totalRows - 1) {
                nr = row + 1;
                nc = 0;
            }
        } else if (direction === "prev") {
            if (col > 0) nc = col - 1;
            else if (row > 0) {
                nr = row - 1;
                nc = totalCols - 1;
            }
        }
        focusCell(nr, nc);
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case "ArrowUp":
                e.preventDefault();
                move("up");
                break;
            case "ArrowDown":
                e.preventDefault();
                move("down");
                break;
            case "ArrowLeft":
                // RTL: visual left = next
                e.preventDefault();
                move("next");
                break;
            case "ArrowRight":
                // RTL: visual right = previous
                e.preventDefault();
                move("prev");
                break;
            case "Enter":
                e.preventDefault();
                if (focusPos && focusPos.row === totalRows - 1) {
                    // On the last row → add a new row and jump to its first cell.
                    addRow();
                    const newRowIdx = totalRows; // index of the row that will be appended
                    setTimeout(() => focusCell(newRowIdx, 0), 30);
                } else {
                    move("down");
                }
                break;
            default:
                break;
        }
    };

    // hide mobile bar when focus leaves the table
    useEffect(() => {
        const handler = () => {
            setTimeout(() => {
                if (!tableRef.current) return;
                if (!tableRef.current.contains(document.activeElement)) {
                    setFocusPos(null);
                }
            }, 60);
        };
        document.addEventListener("focusout", handler);
        return () => document.removeEventListener("focusout", handler);
    }, []);

    const isFocused = (r, c) => focusPos && focusPos.row === r && focusPos.col === c;

    return (
        <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)]">
            <div className={`flex items-center justify-between px-4 py-3 text-white sm:px-6 sm:py-3.5 ${headerClass}`}>
                <h3 className="font-heading text-lg font-bold tracking-wide sm:text-xl" data-testid={`${testIdPrefix}-title`}>
                    {title}
                </h3>
                <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold sm:px-3 sm:text-xs">
                    {rows.length} {rows.length === 1 ? t.rowsOne : t.rowsMany}
                </span>
            </div>

            <div className="overflow-x-auto subtle-scroll" ref={tableRef}>
                <table className="w-full min-w-[760px] border-collapse text-sm">
                    <thead>
                        <tr className="bg-neutral-50/80 text-neutral-600">
                            {COLUMNS.map((c) => (
                                <th
                                    key={c.key}
                                    className={`border-b border-neutral-200 px-3 py-3 text-start text-[12px] font-bold uppercase tracking-wider ${c.className}`}
                                >
                                    {c.label}
                                </th>
                            ))}
                            {!readOnly ? (
                                <th className="w-[44px] border-b border-neutral-200 px-2 py-3"></th>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length + (readOnly ? 0 : 1)} className="px-6 py-10 text-center text-sm text-neutral-400">
                                    {t.emptyTable}
                                </td>
                            </tr>
                        ) : null}
                        {rows.map((row, idx) => {
                            const result = computeResult(row);
                            // build a column index that matches editableCols ordering
                            let editableIdx = -1;
                            return (
                                <tr
                                    key={row.id}
                                    className="group fade-up border-b border-neutral-100 last:border-0 hover:bg-neutral-50/50"
                                    data-testid={`${testIdPrefix}-row-${idx}`}
                                >
                                    {COLUMNS.map((c) => {
                                        if (c.computed) {
                                            return (
                                                <td key={c.key} className={`px-3 py-1.5 ${c.className}`}>
                                                    <div
                                                        className="rounded-md bg-amber-50/60 px-3 py-2 font-numbers text-sm font-semibold text-amber-900"
                                                        data-testid={`${testIdPrefix}-row-${idx}-result`}
                                                    >
                                                        {result || "—"}
                                                    </div>
                                                </td>
                                            );
                                        }
                                        editableIdx += 1;
                                        const colIdx = editableIdx;
                                        const focused = isFocused(idx, colIdx);
                                        const karatLocked = karatLock && c.key === "karat_type";
                                        // Karat one-time-edit rule (Mansori spec): when karatLock
                                        // is on, allow editing karat even on locked rows, but only
                                        // until the user saves a change → then karat_edited=true
                                        // and karat re-locks with the rest of the row.
                                        const isKaratOneTime = karatLock && c.key === "karat" && row.karat_edited !== true;
                                        const cellLocked = karatLocked || (!isKaratOneTime && (readOnly || row.locked === true));
                                        return (
                                            <td
                                                key={c.key}
                                                className={`px-2 py-1.5 ${c.className} ${
                                                    focused ? "bg-amber-50/70 ring-1 ring-amber-300" : ""
                                                } ${row.locked && !readOnly ? "bg-neutral-50/80" : ""}`}
                                            >
                                                {cellLocked ? (
                                                    <div
                                                        className={`px-2 py-2 ${c.numeric ? "font-numbers" : ""} ${row.locked && !readOnly ? "text-neutral-800" : ""} ${karatLocked ? "text-amber-800 font-bold" : ""}`}
                                                        data-testid={`${testIdPrefix}-row-${idx}-${c.key}`}
                                                    >
                                                        {karatLocked ? karatLock : (row[c.key] || "—")}
                                                    </div>
                                                ) : (
                                                    <input
                                                        className={`cell-input ${c.numeric ? "numbers" : ""} ${isKaratOneTime ? "ring-1 ring-amber-300 bg-amber-50/40" : ""}`}
                                                        value={row[c.key] || ""}
                                                        inputMode={c.numeric ? "decimal" : "text"}
                                                        onChange={(e) => updateRow(idx, c.key, e.target.value)}
                                                        onKeyDown={handleKeyDown}
                                                        onFocus={() => setFocusPos({ row: idx, col: colIdx })}
                                                        data-cell-row={idx}
                                                        data-cell-col={colIdx}
                                                        data-testid={`${testIdPrefix}-row-${idx}-${c.key}`}
                                                        title={isKaratOneTime ? "قابل للتعديل مرة واحدة — يُقفل بعد الحفظ" : undefined}
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}
                                    {!readOnly && !row.locked ? (
                                        <td className="px-2 py-1.5">
                                            <button
                                                type="button"
                                                onClick={() => removeRow(idx)}
                                                className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 opacity-60 transition hover:bg-red-50 hover:text-red-600 hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
                                                data-testid={`${testIdPrefix}-row-${idx}-delete`}
                                                aria-label={t.deleteRow}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    ) : null}
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        {kind === "client" ? null : (
                            <tr className="bg-neutral-50/60">
                                <td colSpan={COLUMNS.length - 1} className="px-4 py-3 text-end text-sm font-semibold text-neutral-600">
                                    {t.totalFees}
                                </td>
                                <td className="px-4 py-3 font-numbers text-sm font-bold text-neutral-900" data-testid={`${testIdPrefix}-total-fees`}>
                                    {totalFees ? totalFees.toLocaleString("en-US", { maximumFractionDigits: 3 }) : "0"}
                                </td>
                                {!readOnly ? <td></td> : null}
                            </tr>
                        )}
                    </tfoot>
                </table>
            </div>

            {!readOnly ? (
                <div className="border-t border-neutral-100 bg-neutral-50/40 px-4 py-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={addRow}
                        className="border-dashed"
                        data-testid={`${testIdPrefix}-add-row-btn`}
                    >
                        <Plus className="ms-1 h-4 w-4" />
                        {t.addRow}
                    </Button>
                </div>
            ) : null}

            {/* Mobile / on-screen navigation accessory bar — visible while a cell in this table is focused */}
            {!readOnly && focusPos ? (
                <div
                    className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-2 border-t border-neutral-200 bg-white/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-white/85 sm:hidden"
                    style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
                    onMouseDown={(e) => e.preventDefault()}
                    data-testid={`${testIdPrefix}-cell-nav-bar`}
                >
                    <div className="text-[11px] font-medium text-neutral-500">
                        {t.rowWord} <span className="font-numbers font-semibold text-neutral-800">{focusPos.row + 1}</span>
                        <span className="mx-1">/</span>
                        {t.cellWord} <span className="font-numbers font-semibold text-neutral-800">{focusPos.col + 1}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => move("up")}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 active:bg-neutral-100"
                            aria-label={t.navUp}
                            data-testid={`${testIdPrefix}-nav-up`}
                        >
                            ↑
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => move("down")}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 active:bg-neutral-100"
                            aria-label={t.navDown}
                            data-testid={`${testIdPrefix}-nav-down`}
                        >
                            ↓
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => move("prev")}
                            className="inline-flex h-9 items-center gap-1 rounded-lg border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 active:bg-neutral-100"
                            data-testid={`${testIdPrefix}-nav-prev`}
                        >
                            <ChevronRight className="h-4 w-4" />
                            {t.navPrev}
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => move("next")}
                            className="inline-flex h-9 items-center gap-1 rounded-lg bg-neutral-900 px-3 text-sm font-medium text-white active:bg-neutral-800"
                            data-testid={`${testIdPrefix}-nav-next`}
                        >
                            {t.navNext}
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            ) : null}
        </section>
    );
}

export { computeResult };
